import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, List, ListItemButton, ListItemText, Paper, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

import Header from "../../../components/Header";
import { RESOLUTION_META, chipSx, metaFor, apiError } from "./zkStatusStyles";
import { backButtonSx, cancelButtonSx } from "../hpcAdminStatusStyles";
import { zkApi as api } from "./zkApi";
import ZkGymDoor from "./ZkGymDoor";

// Compact version of the HPC secondary button for the per-row actions.
const rowActionSx = { ...cancelButtonSx, minWidth: 0, px: 1, py: 0.25 };

const ATTENTION = ["needs_review", "unmapped"];
const DEVICE_REFRESH_MS = 60000;

const totalPunches = (punches) => Object.values(punches || {}).reduce((a, b) => a + b, 0);

// The backend already masks these; masked again here so a password never reaches the screen.
const maskSecrets = (text) => (text || "").replace(/(^|[\s&,])(pass(?:word|wd))=[^\t\r\n&]*/gim, "$1$2=***");

const SNAPSHOT_TABLES = ["all", "user", "userauthorize", "timezone"];
const SNAPSHOT_WAIT_MS = 30000;
const EMPTY_SNAPSHOTS = { items: [], counts: {}, commands: [] };

// One line per recent DATA QUERY request: tells "waiting" apart from "device answered with 0 rows".
const commandText = (c) => {
  if (c.status === "pending") return "queued - waiting for the device";
  if (c.status === "sent") return "sent - waiting for the answer";
  const rows = c.snapshots ? `${c.snapshots} snapshot(s) stored` : "device answered but sent no rows";
  return `${c.status} · Return=${c.return_code ?? "-"} · ${rows}`;
};

function DeviceCard({ device, onRefreshUsers, onReadTable, onShowSnapshots, onShowRefused, busy }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{device.name || "Attendance device"}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>SN {device.serial_number}</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Chip size="small" label={device.online ? "Online" : "Offline"}
            sx={chipSx(device.online ? "#4caf50" : "#f44336")} />
          {!device.enabled && <Chip size="small" label="Disabled" sx={chipSx("#9e9e9e")} />}
          {device.refused_today > 0 && (
            <Tooltip title="Scans refused at the door today (access denied)">
              <Chip size="small" label={`Refused today: ${device.refused_today}`} sx={chipSx("#ff9800")}
                onClick={onShowRefused} />
            </Tooltip>
          )}
        </Box>
      </Box>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={6}><Typography variant="body2">Last seen: <b>{device.last_seen || "never"}</b></Typography></Grid>
        <Grid item xs={6}><Typography variant="body2">Last punch: <b>{device.last_punch_local || "-"}</b></Typography></Grid>
        <Grid item xs={6}><Typography variant="body2">Punches received: <b>{device.raw_punches}</b></Typography></Grid>
        <Grid item xs={6}>
          <Typography variant="body2">
            Waiting to be linked: <b style={{ color: device.unmapped_punches ? "#ff9800" : undefined }}>{device.unmapped_punches}</b>
          </Typography>
        </Grid>
        <Grid item xs={12}><Typography variant="caption" sx={{ opacity: 0.7 }}>Firmware {device.firmware || "-"}</Typography></Grid>
      </Grid>
      <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Tooltip title="Ask the device to send its user list (PIN + name). Delivered on its next heartbeat.">
          <span>
            <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<SyncOutlinedIcon />}
              disabled={busy || !device.enabled} onClick={() => onRefreshUsers(device.serial_number)}>
              Refresh user list
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Read-only: ask the device for its user access table (userauthorize). Nothing is changed on the device.">
          <span>
            <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<TableViewOutlinedIcon />}
              disabled={busy || !device.enabled}
              onClick={() => onReadTable(device.serial_number, "query_userauthorize", "userauthorize")}>
              Read access table
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Read-only: ask the device for its time rules (timezone). Nothing is changed on the device.">
          <span>
            <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<ScheduleOutlinedIcon />}
              disabled={busy || !device.enabled}
              onClick={() => onReadTable(device.serial_number, "query_timezone", "timezone")}>
              Read time rules
            </Button>
          </span>
        </Tooltip>
        <Button size="small" variant="outlined" sx={backButtonSx}
          onClick={() => onShowSnapshots(device.serial_number, "all")}>
          View snapshots
        </Button>
      </Box>
    </Paper>
  );
}

export default function ZkDevice() {
  const [devices, setDevices] = useState([]);
  const [pins, setPins] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingPins, setLoadingPins] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("attention");
  const [busy, setBusy] = useState(false);

  const [mapping, setMapping] = useState(null); // pin row being mapped
  const [membershipId, setMembershipId] = useState("");

  // Deep link: ?tab=door&filter=blocked opens the gym door tab with that door-access filter.
  const [searchParams] = useSearchParams();
  const accessFilter = searchParams.get("filter") || undefined;
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "door" ? "door" : "users"));

  const [snapshotView, setSnapshotView] = useState(null); // { sn, table } while the dialog is open
  const [snapshots, setSnapshots] = useState(EMPTY_SNAPSHOTS);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [snapshotError, setSnapshotError] = useState("");
  const snapshotRequest = useRef(0); // only the latest request may update the dialog

  const loadDevices = useCallback(async () => {
    setLoadingDevices(true);
    try {
      const res = await api("get", "/hpc/admin/zk/devices");
      setDevices(res.data || []);
      setError("");
    } catch (e) {
      setError(apiError(e, "Could not load devices"));
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  const loadPins = useCallback(async () => {
    setLoadingPins(true);
    try {
      const res = await api("get", "/hpc/admin/zk/pins");
      setPins(res.data || []);
    } catch (e) {
      toast.error(apiError(e, "Could not load device users"));
    } finally {
      setLoadingPins(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
    loadPins();
    const t = setInterval(loadDevices, DEVICE_REFRESH_MS);
    return () => clearInterval(t);
  }, [loadDevices, loadPins]);

  const visiblePins = useMemo(
    () => (filter === "attention" ? pins.filter((p) => ATTENTION.includes(p.resolution)) : pins),
    [pins, filter],
  );
  const attentionCount = pins.filter((p) => ATTENTION.includes(p.resolution)).length;

  const refreshUsers = async (sn) => {
    setBusy(true);
    try {
      await api("post", `/hpc/admin/zk/devices/${encodeURIComponent(sn)}/command`, { data: { action: "query_users" } });
      toast.info("Requested. The device sends its user list within ~30 seconds; the list reloads automatically.");
      setTimeout(() => { loadPins(); loadDevices(); }, 30000);
    } catch (e) {
      toast.error(apiError(e, "Could not send the request"));
    } finally {
      setBusy(false);
    }
  };

  const loadSnapshots = useCallback(async (sn, table) => {
    const request = ++snapshotRequest.current;
    setLoadingSnapshots(true);
    try {
      const params = { serial_number: sn, limit: 20 };
      if (table !== "all") params.table = table;
      const res = await api("get", "/hpc/admin/zk/snapshots", { params });
      if (request !== snapshotRequest.current) return;
      setSnapshots({ ...EMPTY_SNAPSHOTS, ...(res.data || {}) });
      setSnapshotError("");
    } catch (e) {
      if (request !== snapshotRequest.current) return;
      setSnapshots(EMPTY_SNAPSHOTS);
      setSnapshotError(apiError(e, "Could not load snapshots"));
    } finally {
      if (request === snapshotRequest.current) setLoadingSnapshots(false);
    }
  }, []);

  const snapshotCount = (table) => (table === "all"
    ? Object.values(snapshots.counts).reduce((a, b) => a + b, 0)
    : snapshots.counts[table] || 0);

  const showSnapshots = (sn, table = "all") => {
    setSnapshotView({ sn, table });
    loadSnapshots(sn, table);
  };

  const readTable = async (sn, action, table) => {
    setBusy(true);
    try {
      await api("post", `/hpc/admin/zk/devices/${encodeURIComponent(sn)}/command`, { data: { action } });
      toast.info(`Requested ${table} (read-only). The result is shown in ~30 seconds.`);
      setTimeout(() => showSnapshots(sn, "all"), SNAPSHOT_WAIT_MS);
    } catch (e) {
      toast.error(apiError(e, "Could not send the request"));
    } finally {
      setBusy(false);
    }
  };

  const saveMapping = async (row, mapType, memberId) => {
    setBusy(true);
    try {
      const res = await api("put", "/hpc/admin/zk/pin-map", {
        data: { pin: row.pin, map_type: mapType, membership_id: memberId || undefined, note: row.device_name || undefined },
      });
      const r = res.data?.results || {};
      const credited = (r.check_in || 0) + (r.repeat || 0);
      toast.success(`PIN ${row.pin} saved${credited ? ` — ${r.check_in || 0} attendance day(s) added` : ""}`);
      setMapping(null);
      setMembershipId("");
      loadPins();
      loadDevices();
    } catch (e) {
      toast.error(apiError(e, "Could not save the mapping"));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { field: "pin", headerName: "PIN", width: 90 },
    { field: "device_name", headerName: "Name on device", width: 160, valueGetter: (v) => v || "-" },
    {
      field: "resolution", headerName: "Status", width: 150,
      renderCell: ({ value }) => {
        const m = metaFor(RESOLUTION_META, value);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip size="small" label={m.label} sx={chipSx(m.color)} />
          </Box>
        );
      },
    },
    {
      field: "member", headerName: "Linked member", flex: 1, minWidth: 240, sortable: false,
      renderCell: ({ row }) => {
        if (row.member) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
              {row.name_match === true && <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#4caf50" }} />}
              {row.name_match === false && <HighlightOffIcon fontSize="small" sx={{ color: "#ff9800" }} />}
              <span>{row.member.full_name} <span style={{ opacity: 0.7 }}>({row.member.membership_id})</span></span>
            </Box>
          );
        }
        if (row.suggestions?.length) {
          return (
            <span style={{ opacity: 0.8 }}>
              Possible: {row.suggestions[0].full_name} ({row.suggestions[0].membership_id})
              {row.suggestions.length > 1 ? ` +${row.suggestions.length - 1} more` : ""}
            </span>
          );
        }
        return <span style={{ opacity: 0.6 }}>-</span>;
      },
    },
    { field: "punches", headerName: "Scans", width: 80, type: "number", valueGetter: (v, row) => totalPunches(row.punches) },
    {
      field: "actions", headerName: "", width: 290, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
          <Button size="small" variant="contained" disabled={busy}
            onClick={() => { setMapping(row); setMembershipId(""); }}>
            Link
          </Button>
          <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy || row.resolution === "staff"} onClick={() => saveMapping(row, "staff")}>Staff</Button>
          <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy || row.resolution === "ignored"} onClick={() => saveMapping(row, "ignore")}>Ignore</Button>
          {["mapped", "staff", "ignored"].includes(row.resolution) && (
            <Tooltip title="Remove the manual setting and go back to automatic matching by number + name">
              <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy} onClick={() => saveMapping(row, "auto")}>Reset</Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <ToastContainer position="top-center" theme="dark" />
      <Header title="ATTENDANCE DEVICE" subtitle="ZKTeco device status and linking device users (PINs) to HPU members" />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loadingDevices && !devices.length && <Grid item xs={12}><CircularProgress size={24} /></Grid>}
        {devices.map((d) => (
          <Grid item xs={12} md={6} key={d.serial_number}>
            <DeviceCard device={d} onRefreshUsers={refreshUsers} onReadTable={readTable}
              onShowSnapshots={showSnapshots} onShowRefused={() => setTab("door")} busy={busy} />
          </Grid>
        ))}
        {!loadingDevices && !devices.length && !error && (
          <Grid item xs={12}><Alert severity="info">No device has connected yet.</Alert></Grid>
        )}
      </Grid>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }} textColor="inherit" indicatorColor="secondary">
        <Tab value="users" label="Device users" />
        <Tab value="door" label="Gym door" />
      </Tabs>

      {tab === "door" && <ZkGymDoor devices={devices} accessFilter={accessFilter} />}

      {tab === "users" && (<>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Device users</Typography>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)}>
          <ToggleButton value="attention">Needs attention ({attentionCount})</ToggleButton>
          <ToggleButton value="all">All ({pins.length})</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />} onClick={() => { loadPins(); loadDevices(); }}>
          Reload
        </Button>
      </Box>

      <Box sx={{ height: "60vh" }}>
        <DataGrid
          rows={visiblePins}
          columns={columns}
          loading={loadingPins}
          getRowId={(r) => r.pin}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: filter === "attention" ? "Nothing needs attention" : "No device users yet" }}
        />
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        Members are linked automatically when the device PIN equals their HPU number and the name matches.
        Linking a PIN here also re-checks its earlier scans.
      </Typography>
      </>)}

      <Dialog open={!!mapping} onClose={() => setMapping(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link PIN {mapping?.pin} ({mapping?.device_name || "no name"}) to a member</DialogTitle>
        <DialogContent dividers>
          {mapping?.suggestions?.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Possible matches by name (best first). Check with staff before linking:
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {mapping.suggestions.map((s) => (
                  <ListItemButton key={s.membership_id} selected={membershipId === s.membership_id}
                    onClick={() => setMembershipId(s.membership_id)}>
                    <ListItemText primary={s.full_name} secondary={`${s.membership_id} · ${s.status}`} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
          <TextField fullWidth size="small" label="Membership ID" placeholder="HPC-2026-00062"
            value={membershipId} onChange={(e) => setMembershipId(e.target.value.trim().toUpperCase())} />
          <Typography variant="caption" sx={{ display: "block", mt: 1, opacity: 0.7 }}>
            Pick a suggestion or type the membership ID. Earlier scans of this PIN that were not credited yet
            will be added to this member.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapping(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !membershipId}
            onClick={() => saveMapping(mapping, "member", membershipId)}>
            Link member
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!snapshotView} onClose={() => setSnapshotView(null)} maxWidth="md" fullWidth>
        <DialogTitle>Device table snapshots (read-only) · SN {snapshotView?.sn}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <ToggleButtonGroup size="small" exclusive value={snapshotView?.table || "all"}
              onChange={(e, v) => v && showSnapshots(snapshotView.sn, v)}>
              {SNAPSHOT_TABLES.map((t) => <ToggleButton key={t} value={t}>{t} ({snapshotCount(t)})</ToggleButton>)}
            </ToggleButtonGroup>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />}
              disabled={loadingSnapshots} onClick={() => loadSnapshots(snapshotView.sn, snapshotView.table)}>
              Reload
            </Button>
          </Box>
          {snapshotError && <Alert severity="error" sx={{ mb: 2 }}>{snapshotError}</Alert>}
          {snapshots.commands.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Recent read requests</Typography>
              {snapshots.commands.slice(0, 5).map((c) => (
                <Typography key={c.id} variant="body2" sx={{ opacity: 0.85 }}>
                  cmd {c.id} · {c.tablename || "?"} · {c.created_at} · {commandText(c)}
                </Typography>
              ))}
            </Box>
          )}
          {loadingSnapshots && <CircularProgress size={24} />}
          {!loadingSnapshots && !snapshotError && !snapshots.items.length && (
            <Alert severity="info">
              {snapshotView?.table && snapshotView.table !== "all" && snapshotCount("all") > 0
                ? `No ${snapshotView.table} snapshots. Other tables have ${snapshotCount("all")} - choose "all".`
                : "No snapshots yet. If you just sent a request, the device answers on its next heartbeat - reload in a moment."}
            </Alert>
          )}
          {!loadingSnapshots && snapshots.items.map((s) => (
            <Box key={s.id} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>{s.received_at}</b> · {s.tablename} · {s.source === "cdata" ? "pushed by device" : "query result"}
                {s.cmd_id ? ` · cmd ${s.cmd_id}` : ""} · {s.byte_len} bytes
              </Typography>
              <Box component="pre" sx={{
                m: 0, p: 1.5, maxHeight: 300, overflow: "auto", borderRadius: 1, fontSize: 12,
                fontFamily: "monospace", whiteSpace: "pre", bgcolor: "rgba(127,127,127,0.12)",
              }}>
                {maskSecrets(s.raw_text) || "(empty)"}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
